/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  LayoutDashboard, 
  FolderPlus, 
  LogOut, 
  Shield,
  User,
  Globe,
  Lock
} from 'lucide-react';
import { Button } from './ui/button';
import ZoneProLogo from './ZoneProLogo';
import VersionStamp from './VersionStamp';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/projects/new', icon: FolderPlus, label: t('nav.newProject') },
  ];

  const adminItems = [
    { path: '/admin', icon: Shield, label: t('nav.admin') },
  ];

  return (
    <aside className="w-64 min-h-screen flex flex-col bg-zonepro-primary text-white" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <Link to="/dashboard">
          <ZoneProLogo variant="white" size="md" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6">
        <div className="px-3 mb-2">
          <p className="text-xs uppercase tracking-wider text-white/40 px-4 font-medium">Navigation</p>
        </div>
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
              location.pathname === item.path 
                ? 'bg-zonepro-accent text-white' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            data-testid={`nav-${item.path.replace('/', '')}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </Link>
        ))}

        {isAdmin && (
          <>
            <div className="px-3 mt-6 mb-2">
              <p className="text-xs uppercase tracking-wider text-white/40 px-4 font-medium">Admin</p>
            </div>
            {adminItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  location.pathname.startsWith(item.path) 
                    ? 'bg-zonepro-accent text-white' 
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </>
        )}
      </nav>

      {/* Language Toggle */}
      <div className="px-4 py-3 border-t border-white/10">
        <button
          onClick={toggleLanguage}
          className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="language-toggle"
        >
          <div className="flex items-center gap-2 text-white/70">
            <Globe className="w-4 h-4" />
            <span className="text-sm">{language === 'fr' ? 'Français' : 'English'}</span>
          </div>
          <div className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5">
            <span className={`text-xs font-medium ${language === 'fr' ? 'text-white' : 'text-white/50'}`}>FR</span>
            <span className="text-white/30">|</span>
            <span className={`text-xs font-medium ${language === 'en' ? 'text-white' : 'text-white/50'}`}>EN</span>
          </div>
        </button>
      </div>

      {/* User section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-zonepro-accent/30 flex items-center justify-center">
            <User className="w-5 h-5 text-zonepro-highlight" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-white">{user?.full_name}</p>
            <p className="text-xs text-white/50 truncate">{user?.email}</p>
          </div>
        </div>
        <Link
          to="/account"
          className="flex items-center gap-2 px-4 py-2 mb-2 text-sm rounded-md text-white/70 hover:text-white hover:bg-white/10"
          data-testid="nav-account"
        >
          <Lock className="w-4 h-4" />
          Mon compte
        </Link>
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-white/70 hover:text-white hover:bg-white/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          {t('nav.logout')}
        </Button>
        <VersionStamp className="block px-4 mt-3 text-[10px] text-white/40" testId="version-menu" />
      </div>
    </aside>
  );
};

export default Sidebar;
